import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { entityRefClass } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function AdminBaseTTPage() {
  const auth = useAuth()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [linkIdFilter, setLinkIdFilter] = useState('')

  const buildParams = () => {
    const params: Record<string, string> = { page: String(page), page_size: String(pageSize) }
    if (linkIdFilter) params.link_id = linkIdFilter
    return params
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-base-tt', page, pageSize, linkIdFilter],
    queryFn: () => api.adminListBaseTT(buildParams()),
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  const truncate = (value: string | null, maxLen: number) => {
    if (value == null) return '—'
    if (value.length <= maxLen) return value
    return value.slice(0, maxLen) + '...'
  }

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Base TT</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Link ID</Label>
            <Input
              type="number"
              placeholder="Filter by link ID"
              className="w-40"
              value={linkIdFilter}
              onChange={(e) => { setLinkIdFilter(e.target.value); setPage(1) }}
            />
          </div>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading base TT...
          </div>
        )}

        {error && (
          <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
            Error loading base TT: {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">Link ID</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Base TT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-muted-foreground">No base TT records found</TableCell>
                    </TableRow>
                  ) : (
                    data.items.map((bt, i) => (
                      <TableRow key={`${bt.link_id}-${i}`}>
                        <TableCell className="font-medium">
                          <Link to={buildAppHref(`/admin/links/${bt.link_id}`, auth)} className={entityRefClass}>{bt.link_id}</Link>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <span title={bt.base_tt ?? undefined}>{truncate(bt.base_tt, 100)}</span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {data.page} of {totalPages} ({data.total} total)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
